import { useEffect, useState } from 'react';
import { portalApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { fmtEdTime } from '@/lib/tz';
import { toast } from 'sonner';
import { ListChecks, CheckSquare, Square, Camera, Check, Clock, Repeat } from 'lucide-react';

export default function PortalChecklists() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // { assignment, checklist, completion }
  const [saving, setSaving] = useState(null);

  const load = () => portalApi.get('/checklists/me/today').then(({ data }) => setItems(data.checklists)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const start = async (entry) => {
    try {
      const { data } = await portalApi.post(`/checklists/me/${entry.assignment.id}/start`);
      setActive({ ...entry, completion: data.completion });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const respOf = (itemId) => (active?.completion?.responses || []).find((r) => r.itemId === itemId) || {};

  const save = async (itemId, patch, file) => {
    setSaving(itemId);
    try {
      const cur = respOf(itemId);
      const form = new FormData();
      form.append('itemId', itemId);
      form.append('checked', patch.checked !== undefined ? patch.checked : (cur.checked || false));
      form.append('text', patch.text !== undefined ? patch.text : (cur.text || ''));
      if (file) form.append('photo', file);
      const { data } = await portalApi.patch(`/checklists/me/completions/${active.completion.id}/respond`, form);
      setActive((a) => ({ ...a, completion: data.completion }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(null);
    }
  };

  const finish = async () => {
    try {
      await portalApi.post(`/checklists/me/completions/${active.completion.id}/finish`);
      toast.success('Checklist completed — thank you!');
      setActive(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ===== Active completion view =====
  if (active) {
    const { checklist, completion } = active;
    return (
      <div className="space-y-4" data-testid="checklist-active">
        <button onClick={() => { setActive(null); load(); }} className="text-sm text-saffron">← Back to checklists</button>
        <div>
          <h1 className="font-heading text-2xl font-700 text-charcoal">{checklist.title}</h1>
          {checklist.description && <p className="text-sm text-muted-ink">{checklist.description}</p>}
          <p className="text-xs text-muted-ink flex items-center gap-1 mt-1"><Clock size={12} /> Started {fmtEdTime(completion.startedAt)} MT</p>
        </div>

        <div className="space-y-2">
          {checklist.items.map((it) => {
            const r = respOf(it.id);
            return (
              <div key={it.id} className="bg-white rounded-2xl border border-border/40 p-4" data-testid="checklist-active-item">
                <div className="flex items-start gap-3">
                  {it.requireCheck ? (
                    <button data-testid={`item-check-${it.id}`} onClick={() => save(it.id, { checked: !r.checked })} className="mt-0.5 shrink-0">
                      {r.checked ? <CheckSquare size={22} className="text-coriander" /> : <Square size={22} className="text-muted-ink" />}
                    </button>
                  ) : <span className="w-1" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-charcoal">{it.label}{!it.optional && <span className="text-red-500"> *</span>}</p>
                    <div className="flex flex-wrap gap-1.5 mt-1 text-[10px] text-muted-ink">
                      {it.requireText && <span className="px-1.5 py-0.5 rounded-full bg-bone">Note</span>}
                      {it.requirePhoto && <span className="px-1.5 py-0.5 rounded-full bg-bone">Photo</span>}
                      {it.optional && <span className="px-1.5 py-0.5 rounded-full bg-bone">Optional</span>}
                    </div>

                    {it.requireText && (
                      <Textarea
                        data-testid={`item-text-${it.id}`}
                        defaultValue={r.text || ''}
                        onBlur={(e) => { if (e.target.value !== (r.text || '')) save(it.id, { text: e.target.value }); }}
                        placeholder="Write your explanation…"
                        className="mt-2 text-sm"
                      />
                    )}

                    {it.requirePhoto && (
                      <div className="mt-2 flex items-center gap-2">
                        <label data-testid={`item-photo-${it.id}`} className="inline-flex items-center gap-1.5 text-sm text-white bg-saffron hover:bg-saffron-hover rounded-lg px-3 py-1.5 cursor-pointer">
                          <Camera size={15} /> {r.photoPath ? 'Retake' : 'Take photo'}
                          <input type="file" accept="image/*" capture="environment" hidden onChange={(e) => e.target.files[0] && save(it.id, {}, e.target.files[0])} />
                        </label>
                        {r.photoPath && <span className="text-xs text-coriander flex items-center gap-1"><Check size={13} /> Photo added</span>}
                      </div>
                    )}

                    {r.respondedAt && <p className="text-[10px] text-muted-ink/70 mt-1.5">Answered {fmtEdTime(r.respondedAt)} MT</p>}
                    {saving === it.id && <p className="text-[10px] text-saffron mt-0.5">Saving…</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <Button data-testid="checklist-finish-btn" onClick={finish} className="w-full bg-coriander hover:bg-coriander/90 gap-1.5"><Check size={16} /> Finish &amp; Submit</Button>
      </div>
    );
  }

  // ===== List view =====
  return (
    <div className="space-y-4" data-testid="portal-checklists">
      <div>
        <h1 className="font-heading text-2xl font-700 text-charcoal flex items-center gap-2"><ListChecks size={22} className="text-saffron" /> Checklists</h1>
        <p className="text-sm text-muted-ink">Your checklists for today.</p>
      </div>

      {loading ? (
        <p className="text-sm text-muted-ink">Loading…</p>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-border/40 p-6 text-center text-sm text-muted-ink" data-testid="portal-checklists-empty">No checklists for you today. 🎉</div>
      ) : (
        <div className="space-y-2">
          {items.map((entry) => {
            const c = entry.checklist;
            const comp = entry.completion;
            const inProgress = comp && comp.status === 'IN_PROGRESS';
            return (
              <div key={entry.assignment.id} className="bg-white rounded-2xl border border-border/40 p-4 flex items-center justify-between gap-3" data-testid="portal-checklist-card">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-charcoal">{c.title}</p>
                  <p className="text-xs text-muted-ink flex items-center gap-2">
                    <span>{c.items.length} items</span>
                    {entry.assignment.recurrence !== 'ONCE' && <span className="flex items-center gap-0.5"><Repeat size={11} /> {entry.assignment.recurrence === 'DAILY' ? 'Daily' : 'Weekly'}</span>}
                  </p>
                </div>
                <Button data-testid="portal-checklist-start-btn" size="sm" onClick={() => start(entry)} className="bg-saffron hover:bg-saffron-hover shrink-0">
                  {inProgress ? 'Continue' : 'Start'}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
